<script setup>
/* global axios */
// Construtor visual de Flow — port fiel do projeto original (React Flow → Vue Flow):
// paleta categorizada à esquerda, canvas com nós customizados + dots/controls/minimap,
// painel de configuração à direita. 31 tipos de nó.
import { ref, markRaw, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import FlowCustomNode from './FlowCustomNode.vue';
import FlowEdge from './FlowEdge.vue';
import FlowNodesSidebar from './FlowNodesSidebar.vue';
import FlowConfigPanel from './flow/ConfigPanel.vue';
import KanbanSelect from './KanbanSelect.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { nodeColor } from './flowNodeTypes';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const flowId = route.params.flowId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const flow = ref(null);
const selected = ref(null);
const saving = ref(false);
const savedMsg = ref(false);
const trigger = ref({ type: 'keyword', value: '' });

// configurações do flow (quem envia as mensagens)
const showSettings = ref(false);
const senderMode = ref('creator');
const senderUserId = ref(null);
// pausa do bot no atendimento humano (defaults = comportamento do original)
const pauseOnAgent = ref(true);
const pauseOnTeam = ref(false);
const resumeOnResolved = ref(true);
const agents = ref([]);
const inboxes = ref([]);
const savingSettings = ref(false);

// Nós exclusivos de API oficial (Template/Botões-Lista WA) só aparecem quando o
// flow está associado a uma caixa WhatsApp Cloud oficial (Channel::Whatsapp +
// provider whatsapp_cloud). Provedores via Channel::Api (Evolution/WAHA) NÃO contam.
const isOfficialWhatsappInbox = i =>
  i &&
  i.channel_type === 'Channel::Whatsapp' &&
  i.provider === 'whatsapp_cloud';
const flowInboxes = computed(() => {
  const tr = trigger.value || {};
  const ids = tr.type === 'inbox' ? tr.value : null;
  // ids específicos → só essas caixas; "todas as caixas" (inbox sem ids) ou
  // outros gatilhos (keyword etc.) → considera todas as caixas da conta.
  if (Array.isArray(ids) && ids.length) {
    return inboxes.value.filter(i => ids.map(String).includes(String(i.id)));
  }
  return inboxes.value;
});
// Caixas mistas: exige que TODAS as caixas associadas sejam oficiais (.every) —
// assim nunca se monta um Template/Botões que quebraria numa caixa não-oficial
// do mesmo flow. Guarda contra lista vazia (`[].every` === true).
const isWhatsapp = computed(
  () =>
    flowInboxes.value.length > 0 &&
    flowInboxes.value.every(isOfficialWhatsappInbox)
);
const senderUserOptions = computed(() => [
  { value: null, label: t('CHATBOT.builder.settings.selectUser') },
  ...agents.value.map(a => ({
    value: a.id,
    label: `${a.name}${a.email ? ` (${a.email})` : ''}`,
  })),
]);

const nodeTypesMap = { custom: markRaw(FlowCustomNode) };
const edgeTypesMap = { kflow: markRaw(FlowEdge) };
const {
  onConnect,
  addEdges,
  addNodes,
  setNodes,
  setEdges,
  removeNodes,
  onNodeClick,
  onPaneClick,
  screenToFlowCoordinate,
  fitView,
  toObject,
} = useVueFlow();

let counter = 1;
const newId = () => {
  const current = counter;
  counter += 1;
  return `n${Date.now()}${current}`;
};

const deleteNode = id => {
  removeNodes(id); // remove o nó e as arestas conectadas
  if (selected.value?.id === id) selected.value = null;
};

// garante type=custom + data.nodeType + callback de exclusão
const decorate = n => {
  const nodeType =
    n.data?.nodeType ||
    (n.type && !['default', 'input', 'output'].includes(n.type)
      ? n.type
      : 'sendText');
  return {
    id: n.id,
    type: 'custom',
    position: n.position || { x: 0, y: 0 },
    data: { ...n.data, nodeType, onDelete: deleteNode },
  };
};

const load = async () => {
  flow.value = (await axios.get(`${base}/flows/${flowId}`)).data.data;
  const fd = flow.value.flow_data || {};
  const loaded =
    fd.nodes && fd.nodes.length
      ? fd.nodes
      : [
          {
            id: newId(),
            position: { x: 320, y: 40 },
            data: { nodeType: 'start' },
          },
        ];
  setNodes(loaded.map(decorate));
  setEdges(
    (fd.edges || []).map(e => ({ ...e, type: 'kflow', animated: true }))
  );
  trigger.value =
    flow.value.trigger && Object.keys(flow.value.trigger).length
      ? { type: 'keyword', value: '', ...flow.value.trigger }
      : { type: 'keyword', value: '' };
  const settings = (flow.value.flow_data || {}).settings || {};
  senderMode.value = settings.senderMode || 'creator';
  senderUserId.value = settings.senderUserId || null;
  const bp = settings.botPause || {};
  pauseOnAgent.value = bp.onAgentAssigned !== false; // default true
  pauseOnTeam.value = bp.onTeamAssigned === true; // default false
  resumeOnResolved.value = bp.resumeOnResolved !== false; // default true
  setTimeout(() => fitView({ padding: 0.2 }), 100);
};

const loadAgents = async () => {
  try {
    agents.value =
      (await axios.get(`/api/v1/accounts/${accountId}/agents`)).data || [];
  } catch (e) {
    agents.value = [];
  }
};
const loadInboxes = async () => {
  try {
    const res = await axios.get(`/api/v1/accounts/${accountId}/inboxes`);
    inboxes.value = res.data.payload || res.data || [];
  } catch (e) {
    inboxes.value = [];
  }
};
const saveSettings = async () => {
  savingSettings.value = true;
  try {
    const obj = toObject();
    const flowData = {
      nodes: obj.nodes.map(n => ({
        id: n.id,
        type: 'custom',
        position: n.position,
        data: { ...n.data, onDelete: undefined },
      })),
      edges: obj.edges,
      settings: {
        senderMode: senderMode.value,
        senderUserId: senderUserId.value,
        botPause: {
          onAgentAssigned: pauseOnAgent.value,
          onTeamAssigned: pauseOnTeam.value,
          resumeOnResolved: resumeOnResolved.value,
        },
      },
    };
    await axios.patch(`${base}/flows/${flowId}`, { flow_data: flowData });
    showSettings.value = false;
  } finally {
    savingSettings.value = false;
  }
};

const addNode = (type, position) => {
  const pos = position || {
    x: 140 + Math.random() * 220,
    y: 160 + Math.random() * 200,
  };
  const data = { nodeType: type };
  if (type === 'switch') data.cases = [{ value: '' }];
  addNodes(decorate({ id: newId(), position: pos, data }));
};

const onDrop = event => {
  const type = event.dataTransfer.getData('application/vueflow');
  if (!type) return;
  const position = screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  });
  addNode(type, position);
};
const onDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

onConnect(params => addEdges({ ...params, type: 'kflow', animated: true }));
onNodeClick(({ node }) => {
  selected.value = node;
});
onPaneClick(() => {
  selected.value = null;
});

const save = async () => {
  saving.value = true;
  try {
    const obj = toObject();
    const sNodes = obj.nodes.map(n => ({
      id: n.id,
      type: 'custom',
      position: n.position,
      data: { ...n.data, onDelete: undefined },
    }));
    const sEdges = obj.edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      label: e.label,
    }));
    await axios.patch(`${base}/flows/${flowId}`, {
      flow_data: {
        nodes: sNodes,
        edges: sEdges,
        settings: {
          senderMode: senderMode.value,
          senderUserId: senderUserId.value,
        },
      },
      trigger: trigger.value,
    });
    savedMsg.value = true;
    setTimeout(() => {
      savedMsg.value = false;
    }, 1500);
  } finally {
    saving.value = false;
  }
};
const toggleActive = async () => {
  await axios.patch(`${base}/flows/${flowId}`, {
    is_active: !flow.value.is_active,
  });
  flow.value.is_active = !flow.value.is_active;
};
const goBack = () => router.push(`/app/accounts/${accountId}/flows`);

onMounted(() => {
  load();
  loadAgents();
  loadInboxes();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- toolbar -->
    <div
      class="flex items-center justify-between px-6 py-4 bg-n-background border-b shrink-0 border-n-weak"
    >
      <div class="flex items-center gap-4">
        <button
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
          :title="t('CHATBOT.builder.backTitle')"
          @click="goBack"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-heading-1 text-n-slate-12">
              {{ flow?.name || 'Flow' }}
            </h1>
            <span
              v-if="flow"
              class="px-2 py-1 text-xs font-semibold rounded-full"
              :class="
                flow.flow_type === 'sequence'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              "
              >{{
                flow.flow_type === 'sequence'
                  ? `🔄 ${t('CHATBOT.flowType.sequence')}`
                  : `⚡ ${t('CHATBOT.flowType.chatbot')}`
              }}</span
            >
          </div>
          <p v-if="flow?.description" class="text-sm text-n-slate-11">
            {{ flow.description }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <span
          v-if="savedMsg"
          class="text-xs text-green-600 dark:text-green-400"
          >{{ t('CHATBOT.builder.savedShort') }}</span
        >
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg"
          :class="
            flow?.is_active
              ? 'bg-n-teal-9 text-white'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
          @click="toggleActive"
        >
          <svg
            v-if="flow?.is_active"
            class="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          <svg
            v-else
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {{
            flow?.is_active
              ? t('CHATBOT.builder.active')
              : t('CHATBOT.builder.inactive')
          }}
        </button>
        <button
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
          :title="t('CHATBOT.builder.settings.title')"
          @click="showSettings = true"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? t('CHATBOT.builder.saving') : t('CHATBOT.builder.save') }}
        </button>
      </div>
    </div>

    <!-- modal de configurações do flow -->
    <div
      v-if="showSettings"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showSettings = false"
    >
      <div
        class="w-[30rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[90vh] flex flex-col"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b shrink-0 border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('CHATBOT.builder.settings.title') }}
          </h2>
          <button
            class="inline-flex items-center justify-center rounded-md w-7 h-7 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
            @click="showSettings = false"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div class="flex-1 min-h-0 p-6 space-y-6 overflow-y-auto">
          <!-- Seção: remetente -->
          <section>
            <div class="flex items-center gap-2">
              <svg
                class="w-4 h-4 text-n-slate-11 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                />
              </svg>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('CHATBOT.builder.settings.senderQuestion') }}
              </h3>
            </div>
            <p class="mt-1 mb-3 text-xs text-n-slate-11">
              {{ t('CHATBOT.builder.settings.senderHint') }}
            </p>
            <div class="space-y-2">
              <label
                v-for="opt in [
                  {
                    value: 'creator',
                    label: 'CHATBOT.builder.settings.creator',
                    hint: 'CHATBOT.builder.settings.creatorHint',
                  },
                  {
                    value: 'user',
                    label: 'CHATBOT.builder.settings.specificUser',
                    hint: 'CHATBOT.builder.settings.specificUserHint',
                  },
                  {
                    value: 'neutral',
                    label: 'CHATBOT.builder.settings.neutral',
                    hint: 'CHATBOT.builder.settings.neutralHint',
                  },
                ]"
                :key="opt.value"
                class="flex items-start gap-3 p-3 transition-all rounded-lg cursor-pointer outline -outline-offset-1"
                :class="
                  senderMode === opt.value
                    ? 'outline-2 outline-n-brand bg-n-alpha-1'
                    : 'outline-1 outline-n-weak hover:outline-n-slate-6'
                "
              >
                <input
                  v-model="senderMode"
                  type="radio"
                  :value="opt.value"
                  class="sr-only"
                />
                <span
                  class="mt-0.5 grid place-items-center w-4 h-4 rounded-full border shrink-0 transition-colors"
                  :class="
                    senderMode === opt.value
                      ? 'border-n-brand'
                      : 'border-n-slate-7'
                  "
                >
                  <span
                    v-show="senderMode === opt.value"
                    class="w-1.5 h-1.5 rounded-full bg-n-brand"
                  />
                </span>
                <span class="flex-1 min-w-0">
                  <span class="block text-sm font-medium text-n-slate-12">
                    {{ t(opt.label) }}
                  </span>
                  <span class="block mt-0.5 text-xs text-n-slate-11">
                    {{ t(opt.hint) }}
                  </span>
                  <span
                    v-if="opt.value === 'user' && senderMode === 'user'"
                    class="block mt-2"
                    @click.stop.prevent
                  >
                    <KanbanSelect
                      v-model="senderUserId"
                      :options="senderUserOptions"
                    />
                  </span>
                </span>
              </label>
            </div>
          </section>

          <!-- Seção: pausa do bot -->
          <section class="pt-2 border-t border-n-weak">
            <div class="flex items-center gap-2">
              <svg
                class="w-4 h-4 text-n-slate-11 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('CHATBOT.builder.settings.botPauseTitle') }}
              </h3>
            </div>
            <p class="mt-1 mb-3 text-xs text-n-slate-11">
              {{ t('CHATBOT.builder.settings.botPauseHint') }}
            </p>
            <div
              class="divide-y rounded-lg outline outline-1 -outline-offset-1 divide-n-weak outline-n-weak"
            >
              <div
                v-for="row in [
                  {
                    model: 'pauseOnAgent',
                    label: 'CHATBOT.builder.settings.pauseOnAgent',
                    hint: 'CHATBOT.builder.settings.pauseOnAgentHint',
                  },
                  {
                    model: 'pauseOnTeam',
                    label: 'CHATBOT.builder.settings.pauseOnTeam',
                    hint: 'CHATBOT.builder.settings.pauseOnTeamHint',
                  },
                  {
                    model: 'resumeOnResolved',
                    label: 'CHATBOT.builder.settings.resumeOnResolved',
                    hint: 'CHATBOT.builder.settings.resumeOnResolvedHint',
                  },
                ]"
                :key="row.model"
                class="flex items-center justify-between gap-3 p-3"
              >
                <div class="min-w-0">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ t(row.label) }}
                  </div>
                  <div class="mt-0.5 text-xs text-n-slate-11">
                    {{ t(row.hint) }}
                  </div>
                </div>
                <Switch
                  v-if="row.model === 'pauseOnAgent'"
                  v-model="pauseOnAgent"
                />
                <Switch
                  v-else-if="row.model === 'pauseOnTeam'"
                  v-model="pauseOnTeam"
                />
                <Switch v-else v-model="resumeOnResolved" />
              </div>
            </div>
          </section>
        </div>

        <div class="flex gap-3 px-6 py-4 border-t shrink-0 border-n-weak">
          <button
            class="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 flex-1"
            :disabled="savingSettings"
            @click="showSettings = false"
          >
            {{ t('CHATBOT.editInboxModal.cancel') }}
          </button>
          <button
            class="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1"
            :disabled="
              savingSettings || (senderMode === 'user' && !senderUserId)
            "
            @click="saveSettings"
          >
            {{
              savingSettings
                ? t('CHATBOT.builder.saving')
                : t('CHATBOT.builder.save')
            }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- paleta -->
      <div class="overflow-y-auto border-r w-64 shrink-0 border-n-weak">
        <FlowNodesSidebar
          :flow-type="flow?.flow_type"
          :is-whatsapp="isWhatsapp"
          @add="addNode"
        />
      </div>

      <!-- canvas -->
      <div
        class="relative flex-1 min-w-0 bg-n-alpha-1"
        @drop="onDrop"
        @dragover="onDragOver"
      >
        <VueFlow
          :node-types="nodeTypesMap"
          :edge-types="edgeTypesMap"
          :default-edge-options="{ type: 'kflow', animated: true }"
        >
          <Background :gap="16" :size="1" pattern-color="#cbd5e1" />
          <Controls />
          <MiniMap
            :node-color="n => nodeColor(n.data?.nodeType).accent"
            pannable
            zoomable
          />
        </VueFlow>
      </div>

      <!-- config -->
      <div
        class="overflow-y-auto bg-n-background border-l w-80 shrink-0 border-n-weak"
      >
        <FlowConfigPanel
          :node="selected"
          :trigger="trigger"
          :account-id="accountId"
          :flow-type="flow?.flow_type"
          @delete="deleteNode"
        />
      </div>
    </div>
  </div>
</template>
